import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { GetTicketNumberSequenceQuery as GetTicketNumberSequence, PurchaseStatus } from '__generated__/graphql';
import { encodeUrl, getServerErrors } from '../../../common/helpers';
import { getRegistrationFiltersInput } from '../../../common/segmentHelpers';
import RegistrationFilters, { RegistrationFilterParams } from '../Registrations/RegistrationFilters';
import UI from '../../../common/UI';
import useEditTicketNumberSequenceMutation from './useEditTicketNumberSequenceMutation';
import useNavigationPrompt from '../../../common/useNavigationPrompt';
import useProject from '../../useProject';
import useValidation from '../../../common/useValidation';

type TicketNumberSequence = GetTicketNumberSequence['project']['ticket_number_sequence'];

const editTicketNumberSequenceCriteriaSchema = yup.object().shape({});

interface EditTicketNumberSequenceCriteriaFormProps {
  sequence: TicketNumberSequence;
}

const EditTicketNumberSequenceCriteriaForm = ({
  sequence,
}: EditTicketNumberSequenceCriteriaFormProps) => {
  const { t } = useTranslation();
  const project = useProject();

  const getValues = (sequence: TicketNumberSequence) => (
    sequence.segment.registration_filters
      ? getRegistrationFiltersInput(sequence.segment.registration_filters)
      : {}
  );

  const form = useForm({
    values: getValues(sequence),
  });

  useNavigationPrompt(form.changed());

  const [editTicketNumberSequence, { error, loading }] = useEditTicketNumberSequenceMutation({
    input: {
      id: sequence.id,
      filters: {
        ticket_categories: form.values.ticket_categories,
        tickets: form.values.tickets,
        time_slots: form.values.time_slots,
        products: form.values.products,
        product_variants: form.values.product_variants,
        promotions: form.values.promotions,
        participant_fields: form.values.participant_fields,
        participant_field_choices: form.values.participant_field_choices,
        invitations: form.values.invitations,
        coupons: form.values.coupons,
        statuses: form.values.statuses,
        has_team: form.values.has_team,
        genders: form.values.genders,
        min_age: form.values.min_age,
        max_age: form.values.max_age,
      },
    },
    onSuccess: (sequence) => {
      form.reset(getValues(sequence));
    },
  });

  const { valid } = useValidation({
    schema: editTicketNumberSequenceCriteriaSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const filters = {
    ...form.values,
    event: sequence.event.id,
    statuses: [PurchaseStatus.complete, PurchaseStatus.incomplete],
  };

  const handleFiltersChange = (update: (filters: RegistrationFilterParams) => RegistrationFilterParams) => {
    form.set((values) => {
      const newValues = update(values);

      return {
        ticket_categories: newValues.ticket_categories || [],
        tickets: newValues.tickets || [],
        time_slots: newValues.time_slots || [],
        products: newValues.products || [],
        product_variants: newValues.product_variants || [],
        promotions: newValues.promotions || [],
        participant_fields: newValues.participant_fields || [],
        participant_field_choices: newValues.participant_field_choices || [],
        invitations: newValues.invitations || [],
        coupons: newValues.coupons || [],
        has_team: newValues.has_team,
        genders: newValues.genders,
        min_age: newValues.min_age,
        max_age: newValues.max_age,
      };
    });
  };

  return (
    <UI.Form onSubmit={() => editTicketNumberSequence()}>
      <UI.FormGrid>
        {sequence.assigned_ticket_number_count > 0 && (
          <UI.Info>
            {t('edit_sequence_ticket_numbers_assigned_description')}
          </UI.Info>
        )}
        <UI.ServerErrors error={error} />
        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>{t('criteria')}</UI.Legend>
          <UI.InputDescription>
            {t('ticket_number_sequence_criteria_description')}
          </UI.InputDescription>
        </UI.Div>
        <RegistrationFilters
          filters={filters}
          onChange={handleFiltersChange}
          types={['age', 'gender', 'tickets', 'products', 'participant_fields', 'invitations', 'coupons', 'team']}
          showResetButton={false}
          showViewParticipantsButton
        />
        <UI.HR />
        <UI.FormButtons
          form={form}
          submitLabel={t('save_ticket_number_sequence')}
          closeTo={`/${project.id}/participants/bib-numbers?${encodeUrl({ event: sequence.event.id })}`}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditTicketNumberSequenceCriteriaForm;
